import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,

  Tooltip,
  IconButton
} from '@mui/material';
import VeersaLoaderGif from "../../common/icons/Veersa-loader.gif";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'react-toastify';
import { getMyAssignedTasks } from '../../redux/actions/task';
import { updateTaskStatus as updateTaskStatusAPI, getTaskMessages } from '../../http/requests/task';
import { toastOptions } from '../../common/utils/toasterOptions';

import './MyAssignedTasks.css';

const MyAssignedTasks = () => {
  const dispatch = useDispatch();
  const { myAssignedTasks, taskLoading } = useSelector(({ TASK }) => TASK);
  
  const [openModal, setOpenModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Drill-down state for messages
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [taskMessages, setTaskMessages] = useState(new Map()); // Store messages for each task
  const [loadingMessages, setLoadingMessages] = useState(new Set()); // Track which tasks are loading messages

  const statusOptions = ['Open', 'In Progress', 'Completed'];

  // Map backend status to UI status for display
  const mapBackendStatusToUI = (backendStatus) => {
    if (!backendStatus) return 'Open';
    const statusLower = backendStatus.toLowerCase();
    if (statusLower === 'open') return 'Open';
    if (statusLower === 'in progress') return 'In Progress';
    if (statusLower === 'completed') return 'Completed';
    return backendStatus; // fallback
  };

  // Map UI status to backend status for API calls
  const mapUIStatusToBackend = (uiStatus) => {
    if (uiStatus === 'Open') return 'open';
    if (uiStatus === 'In Progress') return 'in progress';
    if (uiStatus === 'Completed') return 'completed';
    return uiStatus.toLowerCase();
  };

  useEffect(() => {
    dispatch(getMyAssignedTasks());
  }, [dispatch]);

  useEffect(() => {
    if (myAssignedTasks.length > 0) {
      console.log("MyAssignedTasks - Tasks received:", myAssignedTasks);
      console.log("MyAssignedTasks - First task:", myAssignedTasks[0]);
      console.log("MyAssignedTasks - First task description:", myAssignedTasks[0]?.description);
      console.log("MyAssignedTasks - Description type:", typeof myAssignedTasks[0]?.description);
    }
  }, [myAssignedTasks]);

  const handleStatusChange = (task, status) => {
    if (status !== mapBackendStatusToUI(task.status)) {
      setSelectedTask(task);
      setNewStatus(status);
      setStatusReason('');
      setOpenModal(true);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedTask(null);
    setNewStatus('');
    setStatusReason('');
  };

  const handleStatusUpdate = async () => {
    if (!statusReason.trim()) {
      toast.error('Please provide a reason for the status update', toastOptions);
      return;
    }

    setSubmitting(true);
    
    const requestData = {
      status: mapUIStatusToBackend(newStatus),
      taskStatusReason: statusReason.trim()
    };
    
    try {
      await updateTaskStatusAPI(selectedTask.taskId, requestData);
      
      toast.success('Task status updated successfully', toastOptions);
      handleModalClose();
      // Refresh the task list
      dispatch(getMyAssignedTasks());
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status', toastOptions);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Helper function to truncate text and determine if tooltip is needed
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const shouldShowTooltip = (text, maxLength = 50) => {
    return text && text.length > maxLength;
  };

  // Responsive truncation lengths
  const getTruncationLengths = () => {
    const width = window.innerWidth;
    if (width <= 480) {
      return { title: 20, description: 30 };
    } else if (width <= 768) {
      return { title: 30, description: 40 };
    }
    return { title: 40, description: 60 };
  };

  // Filter tasks based on search term
  const filteredTasks = myAssignedTasks.filter(task => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = task.title?.toLowerCase().includes(searchLower) || false;
    const descriptionMatch = task.description?.toLowerCase().includes(searchLower) || false;
    
    return titleMatch || descriptionMatch;
  });

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Handle drill-down toggle
  const toggleTaskExpansion = async (taskId) => {
    const newExpandedTasks = new Set(expandedTasks);
    if (newExpandedTasks.has(taskId)) {
      newExpandedTasks.delete(taskId);
    } else {
      newExpandedTasks.add(taskId);
      
      // Fetch messages if not already loaded
      if (!taskMessages.has(taskId)) {
        await fetchTaskMessages(taskId);
      }
    }
    setExpandedTasks(newExpandedTasks);
  };

  // Fetch messages for a specific task
  const fetchTaskMessages = async (taskId) => {
    console.log('=== FETCH TASK MESSAGES DEBUG ===');
    console.log('Task ID:', taskId);
    console.log('Task ID type:', typeof taskId);
    console.log('Is Task ID numeric?', !isNaN(taskId) && !isNaN(parseFloat(taskId)));
    
    const newLoadingMessages = new Set(loadingMessages);
    newLoadingMessages.add(taskId);
    setLoadingMessages(newLoadingMessages);

    try {
      console.log('Fetching messages for task ID:', taskId);
      console.log('API endpoint will be: /api/Task/' + taskId + '/messages');
      const response = await getTaskMessages(taskId);
      console.log('API Response for task messages:', response);
      console.log('Response type:', typeof response);
      console.log('Is response array?', Array.isArray(response));
      
      // Handle the response - API returns array directly or wrapped in response.data
      let messagesData = null;
      if (response && Array.isArray(response)) {
        // Direct array response
        messagesData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Wrapped in response.data
        messagesData = response.data;
      }
      
      if (messagesData) {
        console.log('✅ Successfully loaded messages:', messagesData.length);
        const newTaskMessages = new Map(taskMessages);
        newTaskMessages.set(taskId, messagesData);
        setTaskMessages(newTaskMessages);
      } else {
        console.log('⚠️ No messages data found in response');
        // No messages from API, set empty array
        const newTaskMessages = new Map(taskMessages);
        newTaskMessages.set(taskId, []);
        setTaskMessages(newTaskMessages);
      }
    } catch (error) {
      console.error('Error fetching task messages:', error);
      console.error('Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      
      // If API fails, set empty messages array
      const newTaskMessages = new Map(taskMessages);
      newTaskMessages.set(taskId, []);
      setTaskMessages(newTaskMessages);
      
      if (error?.response?.status === 404) {
        console.log('No messages found for task (404)');
      } else if (error?.response?.status !== 404) {
        toast.error('Failed to load task messages', toastOptions);
      }
    } finally {
      const newLoadingMessages = new Set(loadingMessages);
      newLoadingMessages.delete(taskId);
      setLoadingMessages(newLoadingMessages);
    }
  };

  // Check if task is expanded
  const isTaskExpanded = (taskId) => {
    return expandedTasks.has(taskId);
  };

  // Check if task has messages or can show expand button
  const hasMessages = (task) => {
    // Check if we have loaded messages for this task
    if (taskMessages.has(task.taskId)) {
      const messages = taskMessages.get(task.taskId);
      return Array.isArray(messages) && messages.length > 0;
    }
    
    // If not loaded yet, show the expand icon so user can check for messages
    return true;
  };

  // Check if we should show the expand/collapse button
  const shouldShowExpandButton = (task) => {
    // Always show if currently expanded (so user can collapse)
    if (isTaskExpanded(task.taskId)) {
      return true;
    }
    
    // Show if currently loading messages
    if (loadingMessages.has(task.taskId)) {
      return true;
    }
    
    // Once we've checked for messages (whether found or not), always show the button
    // This allows users to expand/collapse even for tasks with no messages
    if (taskMessages.has(task.taskId)) {
      return true;
    }
    
    // Show if we haven't checked for messages yet (allow user to check)
    return true;
  };

  // Get messages for a task
  const getMessagesForTask = (task) => {
    // Return loaded messages if available
    if (taskMessages.has(task.taskId)) {
      return taskMessages.get(task.taskId) || [];
    }
    
    return [];
  };

  if (taskLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <div style={{
          backgroundColor: 'white',
          padding: '15px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img 
            src={VeersaLoaderGif} 
            alt="Loading..." 
            style={{ width: '65px', height: '65px', marginBottom: '12px' }}
          />
          <span style={{ color: '#1976d2', fontSize: '14px', fontWeight: '500' }}>Loading...</span>
        </div>
      </Box>
    );
  }

  return (
    <div className="my-assigned-tasks">
      <div className="tasks-header">
        {/* Search Box */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
          <TextField
            variant="outlined"
            size="medium"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search Tasks....."
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={clearSearch}
              sx={{
                borderRadius: '8px',
                color: '#666',
                borderColor: '#999',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#666'
                }
              }}
            >
              Clear
            </Button>
          )}
        </Box>
        
        {/* Results count */}
        {searchTerm && (
          <Typography variant="body2" sx={{ color: '#666', marginBottom: 1 }}>
            {filteredTasks.length} of {myAssignedTasks.length} tasks found
          </Typography>
        )}
      </div>

      {myAssignedTasks.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No tasks assigned to you
          </Typography>
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No tasks match your search criteria
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Try adjusting your search terms
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className="tasks-table">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f3f8fc' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1773bc' }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1773bc' }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1773bc' }}>
                  Assigned By
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1773bc' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#1773bc' }}>
                  Updated At
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.map((task) => {
                const isCompleted = mapBackendStatusToUI(task.status) === 'Completed';
                return (
                  <React.Fragment key={task.taskId}>
                    <TableRow 
                      hover={!isCompleted}
                      sx={{
                        backgroundColor: isCompleted ? '#e8f5e8' : 'inherit',
                        '&:hover': {
                          backgroundColor: isCompleted ? '#e8f5e8' : '#f8f9fa'
                        }
                      }}
                    >
                      <TableCell sx={{ fontSize: '1.2rem' }} className="title-cell">
                        <Box display="flex" alignItems="center" gap={1}>
                          {shouldShowExpandButton(task) && (
                            <Tooltip title={isTaskExpanded(task.taskId) ? "Hide Messages" : "Show Messages"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => toggleTaskExpansion(task.taskId)}
                                className="message-icon-btn"
                                disabled={loadingMessages.has(task.taskId)}
                                sx={{
                                  padding: '2px',
                                  color: '#1976d2',
                                  transform: isTaskExpanded(task.taskId) ? 'rotate(90deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    transform: isTaskExpanded(task.taskId) ? 'rotate(90deg) scale(1.1)' : 'rotate(0deg) scale(1.1)'
                                  }
                                }}
                              >
                                {loadingMessages.has(task.taskId) ? (
                                  <div style={{
                                    backgroundColor: 'white',
                                    padding: '1px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <img 
                                      src={VeersaLoaderGif} 
                                      alt="Loading..." 
                                      style={{ width: '21px', height: '21px' }}
                                    />
                                  </div>
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {(() => {
                            const { title: titleLength } = getTruncationLengths();
                            const titleContent = shouldShowTooltip(task.title, titleLength) ? (
                              <Tooltip title={task.title} arrow placement="top">
                                <span>
                                  {truncateText(task.title, titleLength)}
                                </span>
                              </Tooltip>
                            ) : (
                              task.title
                            );
                            return titleContent;
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontSize: '1.2rem' }}
                        className="description-cell"
                      >
                        {(() => {
                          const { description: descLength } = getTruncationLengths();
                          
                          // Check if description exists and is not empty string
                          const hasValidDescription = task.description && 
                                                     typeof task.description === 'string' && 
                                                     task.description.trim() !== '';
                          
                          return hasValidDescription ? (
                            shouldShowTooltip(task.description, descLength) ? (
                              <Tooltip title={task.description} arrow placement="top">
                                <span>
                                  {truncateText(task.description, descLength)}
                                </span>
                              </Tooltip>
                            ) : (
                              task.description
                            )
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                              No description provided
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '1.2rem' }}>
                        {task.assignedBy}
                      </TableCell>
                      <TableCell>
                        {isCompleted ? (
                          <Typography 
                            sx={{ 
                              fontSize: '1.2rem', 
                              color: '#2e7d32', 
                              fontWeight: 'bold',
                              padding: '8px 14px',
                              backgroundColor: '#c8e6c9',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}
                          >
                            Completed
                          </Typography>
                        ) : (
                          <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={mapBackendStatusToUI(task.status)}
                              onChange={(e) => handleStatusChange(task, e.target.value)}
                              sx={{ 
                                fontSize: '1.2rem',
                                color: mapBackendStatusToUI(task.status) === 'Open' ? '#d32f2f' : 
                                       mapBackendStatusToUI(task.status) === 'In Progress' ? '#1976d2' : '#2e7d32'
                              }}
                            >
                              {statusOptions.map((status) => (
                                <MenuItem 
                                  key={status} 
                                  value={status}
                                  sx={{
                                    color: status === 'Open' ? '#d32f2f' : 
                                           status === 'In Progress' ? '#1976d2' : '#2e7d32'
                                  }}
                                >
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '1.2rem' }}>
                        {formatDate(task.updatedAt)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Drill-down message rows */}
                    {isTaskExpanded(task.taskId) && (
                      <TableRow className="message-drill-down-row">
                        <TableCell 
                          colSpan={5} 
                          sx={{ 
                            padding: 0,
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #e9ecef'
                          }}
                        >
                          <Box className="messages-drill-down-container">
                            {(() => {
                              const messages = getMessagesForTask(task);
                              return messages.length > 0 ? (
                                <Box sx={{ padding: '16px 24px' }}>
                                  <Typography variant="h6" sx={{ 
                                    marginBottom: '12px', 
                                    color: '#1976d2',
                                    fontSize: '1.1rem',
                                    fontWeight: 600
                                  }}>
                                    Messages ({messages.length})
                                  </Typography>
                                  {messages.map((message, index) => (
                                    <Box key={message.id || index} className="drill-down-message-item" sx={{
                                      marginBottom: index < messages.length - 1 ? '12px' : 0,
                                      padding: '12px',
                                      backgroundColor: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #dee2e6',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        marginBottom: '6px'
                                      }}>
                                        <Typography variant="subtitle2" sx={{ 
                                          color: '#1976d2',
                                          fontWeight: 600,
                                          fontSize: '0.95rem'
                                        }}>
                                          {message.senderName || 'Unknown Sender'}
                                        </Typography>
                                        {message.senderRole && (
                                          <Typography variant="caption" sx={{ 
                                            color: '#6c757d',
                                            fontStyle: 'italic',
                                            fontSize: '0.75rem',
                                            backgroundColor: '#f8f9fa',
                                            padding: '2px 6px',
                                            borderRadius: '4px'
                                          }}>
                                            {message.senderRole}
                                          </Typography>
                                        )}
                                        <Typography variant="caption" sx={{ 
                                          color: '#6c757d',
                                          fontStyle: 'italic',
                                          fontSize: '0.8rem'
                                        }}>
                                          ({formatDate(message.sentAt || message.date || message.createdAt)})
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" sx={{
                                        color: '#495057',
                                        lineHeight: 1.5,
                                        fontSize: '0.9rem'
                                      }}>
                                        {message.messageText || message.message || message.content || 'No message content'}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  padding: '16px 24px',
                                  textAlign: 'center',
                                  color: '#6c757d'
                                }}>
                                  <Typography variant="body2">
                                    No messages found for this task
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Status Update Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleModalClose} 
        className="status-update-dialog"
        maxWidth={false}
      >
        <DialogTitle>
          Update Task Status
        </DialogTitle>
        <DialogContent>
          <div className="task-info">
            <div className="task-label">Task:</div>
            <div className="task-value">{selectedTask?.title}</div>
          </div>
          <div className="task-info">
            <div className="task-label">New Status:</div>
            <div className="task-value">{newStatus}</div>
          </div>
          <TextField
            autoFocus
            margin="dense"
            label="Status Update Reason"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Please provide a reason for this status change..."
            required
            sx={{ marginTop: '1rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleModalClose} 
            disabled={submitting} 
            size="large"
            sx={{ 
              color: '#666',
              borderColor: '#999',
              '&:hover': { 
                backgroundColor: '#f5f5f5',
                borderColor: '#666'
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStatusUpdate} 
            variant="contained" 
            disabled={submitting}
            size="large"
            sx={{ 
              backgroundColor: '#1773bc',
              '&:hover': { backgroundColor: '#125a94' }
            }}
          >
            {submitting ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default MyAssignedTasks;
